import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

export default function TutorPerformanceReports({ apiBase, token, onPageBusyChange }) {
  const navigate = useNavigate();

  // Tab State: "overview" | "history"
  const [activeTab, setActiveTab] = useState("overview");

  // Filter State
  const [daysFilter, setDaysFilter] = useState("7");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedClassroomId, setSelectedClassroomId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  // Data State
  const [classrooms, setClassrooms] = useState([]);
  const [students, setStudents] = useState([]);
  const [periodLabel, setPeriodLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Selection State
  const [selectedUids, setSelectedUids] = useState([]);

  // Modal States
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchTargets, setDispatchTargets] = useState([]);
  const [sharedTutorNote, setSharedTutorNote] = useState("");
  const [sharedSummary, setSharedSummary] = useState("");
  const [dispatching, setDispatching] = useState(false);
  const [dispatchResult, setDispatchResult] = useState(null);

  // History State
  const [, setHistoryReports] = useState([]);
  const [historyBatches, setHistoryBatches] = useState([]);
  const [expandedBatchIds, setExpandedBatchIds] = useState(new Set());
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);

  // ── Load Classrooms ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    fetch(`${apiBase}/api/tutor/classrooms`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => (r.ok ? r.json() : { classrooms: [] }))
      .then((d) => setClassrooms(d.classrooms || []))
      .catch(() => {});
  }, [apiBase, token]);

  // ── Load Overview Data ─────────────────────────────────────────────────────
  const loadOverviewData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      let url = `${apiBase}/api/tutor/reports/students-overview?days=${daysFilter}`;
      if (daysFilter === "custom" && customStartDate) {
        url = `${apiBase}/api/tutor/reports/students-overview?startDate=${customStartDate}&endDate=${customEndDate || new Date().toISOString()}`;
      }
      if (selectedClassroomId) {
        url += `&classroomId=${selectedClassroomId}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load student performance data");

      setStudents(data.students || []);
      setPeriodLabel(data.periodLabel || "Recent Period");
    } catch (err) {
      setError(err.message || "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }, [apiBase, token, daysFilter, customStartDate, customEndDate, selectedClassroomId]);

  useEffect(() => {
    if (activeTab === "overview") {
      loadOverviewData();
    }
  }, [activeTab, loadOverviewData]);

  // ── Load History Data ──────────────────────────────────────────────────────
  const loadHistoryData = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/tutor/reports/history?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        const rawReports = data.reports || [];
        setHistoryReports(rawReports);

        let batches = data.batches || [];
        // Defensive client-side batching fallback if backend returns only flat reports
        if (!batches.length && rawReports.length) {
          const map = new Map();
          for (const r of rawReports) {
            const timeBucket = Math.floor(new Date(r.sentAt || r.createdAt || Date.now()).getTime() / 120000);
            const k = r.batchId || `legacy_${r.tutorUid || "tutor"}_${timeBucket}`;
            if (!map.has(k)) {
              map.set(k, {
                batchId: k,
                sentAt: r.sentAt || r.createdAt,
                periodLabel: r.periodLabel || "Recent Period",
                overallSummary: r.overallSummary || "",
                tutorPersonalNote: r.tutorPersonalNote || "",
                classroomName: r.classroomName || "",
                children: []
              });
            }
            map.get(k).children.push(r);
          }
          batches = Array.from(map.values()).map((b) => {
            const studentNames = b.children.map((c) => c.studentName || "Student");
            return {
              ...b,
              studentCount: b.children.length,
              readCount: b.children.filter((c) => c.readStatus === "read").length,
              deliveredCount: b.children.filter((c) => c.deliveryStatus === "delivered" || c.deliveryStatus === "sent").length,
              failedCount: b.children.filter((c) => c.deliveryStatus === "failed").length,
              studentNames,
              recipientsSummary:
                b.children.length === 1
                  ? studentNames[0]
                  : `${b.children.length} Students: ${studentNames.join(", ")}`
            };
          });
          batches.sort((a, b) => new Date(b.sentAt) - new Date(a.sentAt));
        }

        setHistoryBatches(batches);
        // By default, expand the newest batch so the tutor sees the details right away
        setExpandedBatchIds((prev) => {
          if (prev.size === 0 && batches.length > 0) {
            return new Set([batches[0].batchId]);
          }
          return prev;
        });
      }
    } catch (err) {
      console.warn("Failed to load history:", err);
    } finally {
      setHistoryLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    if (activeTab === "history") {
      loadHistoryData();
    }
  }, [activeTab, loadHistoryData]);

  // ── Toggle Batch Expansion ────────────────────────────────────────────────
  const toggleBatchExpand = (batchId) => {
    setExpandedBatchIds((prev) => {
      const next = new Set(prev);
      if (next.has(batchId)) {
        next.delete(batchId);
      } else {
        next.add(batchId);
      }
      return next;
    });
  };

  const handleToggleAllBatches = (shouldExpand) => {
    if (shouldExpand) {
      setExpandedBatchIds(new Set(historyBatches.map((b) => b.batchId)));
    } else {
      setExpandedBatchIds(new Set());
    }
  };

  // ── Filtered Students ──────────────────────────────────────────────────────
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    const q = searchQuery.toLowerCase().trim();
    return students.filter(
      (s) =>
        (s.name && s.name.toLowerCase().includes(q)) ||
        (s.email && s.email.toLowerCase().includes(q)) ||
        (s.parentEmail && s.parentEmail.toLowerCase().includes(q))
    );
  }, [students, searchQuery]);

  // ── Selection Handlers ─────────────────────────────────────────────────────
  const isAllSelected = filteredStudents.length > 0 && selectedUids.length === filteredStudents.length;

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUids([]);
    } else {
      setSelectedUids(filteredStudents.map((s) => s.studentUid));
    }
  };

  const handleToggleSelectStudent = (uid) => {
    setSelectedUids((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // ── Open Dispatch Modal ────────────────────────────────────────────────────
  const openDispatchModalFor = (targetStudents) => {
    setDispatchTargets(targetStudents);
    setSharedTutorNote("");
    setDispatchResult(null);

    // Build initial default executive summary
    if (targetStudents.length === 1) {
      const s = targetStudents[0];
      const parts = [];
      parts.push(`During ${periodLabel}, ${s.name} actively participated in mathematics learning.`);
      if (s.courseworkStats?.submittedCount > 0) {
        const testAvg = s.courseworkStats.testAvg !== null ? ` with a ${s.courseworkStats.testAvg}% test average` : "";
        parts.push(`Completed ${s.courseworkStats.submittedCount} assigned tasks${testAvg}.`);
      }
      if (s.curriculumPractice?.framesAttemptedCount > 0) {
        parts.push(`Practiced ${s.curriculumPractice.framesAttemptedCount} self-study curriculum questions across ${s.curriculumPractice.chapters?.join(", ") || "core chapters"}.`);
      }
      setSharedSummary(parts.join(" "));
    } else {
      setSharedSummary(
        `During ${periodLabel}, students have been diligently covering curriculum topics and completing assigned coursework. Below is an individual performance review.`
      );
    }

    setShowDispatchModal(true);
  };

  // ── Dispatch Reports via Resend ────────────────────────────────────────────
  const handleDispatchReports = async () => {
    if (!dispatchTargets.length) return;
    setDispatching(true);
    setDispatchResult(null);

    try {
      const reportsPayload = dispatchTargets.map((s) => ({
        studentUid: s.studentUid,
        studentName: s.name,
        parentEmail: s.parentEmail,
        parentName: s.parentName,
        classroomId: selectedClassroomId || null,
        classroomName: classrooms.find((c) => String(c._id) === String(selectedClassroomId))?.name || "",
        periodLabel,
        overallSummary: sharedSummary,
        tutorPersonalNote: sharedTutorNote,
        strengths: s.strengths || [],
        focusAreas: s.focusAreas || [],
        assignmentScores: s.courseworkStats?.assignmentScores || [],
        curriculumPractice: s.curriculumPractice || {}
      }));

      const res = await fetch(`${apiBase}/api/tutor/reports/dispatch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ reports: reportsPayload })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to dispatch reports");

      setDispatchResult(data);
      setSuccessMsg(`Successfully dispatched ${data.dispatchedCount} parent progress report(s)!`);
      setSelectedUids([]);
      loadHistoryData();
      setTimeout(() => setSuccessMsg(""), 6000);
    } catch (err) {
      alert(`Error dispatching feedback: ${err.message}`);
    } finally {
      setDispatching(false);
    }
  };

  // ── History Batches Filter ─────────────────────────────────────────────────
  const filteredBatches = useMemo(() => {
    if (!historySearch.trim()) return historyBatches;
    const q = historySearch.toLowerCase().trim();
    return historyBatches.filter((b) => {
      const matchBatch =
        (b.periodLabel && b.periodLabel.toLowerCase().includes(q)) ||
        (b.overallSummary && b.overallSummary.toLowerCase().includes(q)) ||
        (b.recipientsSummary && b.recipientsSummary.toLowerCase().includes(q));
      const matchChild = b.children?.some(
        (c) =>
          (c.studentName && c.studentName.toLowerCase().includes(q)) ||
          (c.parentEmail && c.parentEmail.toLowerCase().includes(q)) ||
          (c.classroomName && c.classroomName.toLowerCase().includes(q))
      );
      return matchBatch || matchChild;
    });
  }, [historyBatches, historySearch]);

  // Auto-expand all matching batches when searching
  useEffect(() => {
    if (historySearch.trim() && filteredBatches.length > 0) {
      setExpandedBatchIds(new Set(filteredBatches.map((b) => b.batchId)));
    }
  }, [historySearch, filteredBatches]);

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "24px 20px" }}>
      {/* ── Page Header ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <h1 style={{ fontSize: "26px", fontWeight: "800", color: "#0f172a", margin: 0 }}>
              Performance & Parent Feedback
            </h1>
            <span style={{ background: "#e0e7ff", color: "#4338ca", fontSize: "12px", fontWeight: "700", padding: "3px 10px", borderRadius: "12px" }}>
              TUTOR PORTAL
            </span>
          </div>
          <p style={{ color: "#64748b", margin: "6px 0 0 0", fontSize: "14px" }}>
            Review student progress across self-study frames and coursework, then share periodic feedback directly with parents.
          </p>
        </div>

        {/* Global Navigation Shortcut */}
        <div style={{ display: "flex", gap: "10px" }}>
          <button
            type="button"
            className="secondary-button"
            onClick={() => navigate("/tutor/students")}
            style={{ fontSize: "13px", padding: "8px 14px" }}
          >
            👥 Manage Roster
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={() => navigate("/tutor/assignments")}
            style={{ fontSize: "13px", padding: "8px 14px" }}
          >
            📝 Assignments
          </button>
        </div>
      </div>

      {/* Success Notification Alert */}
      {successMsg && (
        <div style={{ padding: "12px 16px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", borderRadius: "8px", marginBottom: "20px", fontWeight: "600", fontSize: "14px" }}>
          ✓ {successMsg}
        </div>
      )}

      {/* ── Main Tab Navigation ── */}
      <div style={{ display: "flex", gap: "24px", borderBottom: "2px solid #e2e8f0", marginBottom: "24px" }}>
        <button
          type="button"
          onClick={() => setActiveTab("overview")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "overview" ? "3px solid #4f46e5" : "3px solid transparent",
            color: activeTab === "overview" ? "#4f46e5" : "#64748b",
            fontWeight: "700",
            fontSize: "15px",
            padding: "10px 4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          📊 Student Performance & Dispatch
          {students.length > 0 && (
            <span style={{ background: activeTab === "overview" ? "#e0e7ff" : "#f1f5f9", color: activeTab === "overview" ? "#4338ca" : "#64748b", fontSize: "11px", padding: "1px 7px", borderRadius: "10px" }}>
              {students.length}
            </span>
          )}
        </button>

        <button
          type="button"
          onClick={() => setActiveTab("history")}
          style={{
            background: "none",
            border: "none",
            borderBottom: activeTab === "history" ? "3px solid #4f46e5" : "3px solid transparent",
            color: activeTab === "history" ? "#4f46e5" : "#64748b",
            fontWeight: "700",
            fontSize: "15px",
            padding: "10px 4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px"
          }}
        >
          📜 Dispatched Feedback History
          {historyBatches.length > 0 && (
            <span style={{ background: activeTab === "history" ? "#e0e7ff" : "#f1f5f9", color: activeTab === "history" ? "#4338ca" : "#64748b", fontSize: "11px", padding: "1px 7px", borderRadius: "10px" }}>
              {historyBatches.length}
            </span>
          )}
        </button>
      </div>

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB 1: STUDENT PERFORMANCE & DISPATCH                                  */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "overview" && (
        <div>
          {/* Controls & Filter Bar */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
              padding: "16px",
              background: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              marginBottom: "20px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
            }}
          >
            {/* Left: Timeframe & Class Filter */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "#475569" }}>Period:</span>
              <div style={{ display: "flex", background: "#f1f5f9", padding: "3px", borderRadius: "8px" }}>
                {[
                  { id: "7", label: "7 Days" },
                  { id: "14", label: "14 Days" },
                  { id: "30", label: "30 Days" },
                  { id: "custom", label: "Custom" }
                ].map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => setDaysFilter(p.id)}
                    style={{
                      border: "none",
                      padding: "5px 12px",
                      borderRadius: "6px",
                      fontSize: "12px",
                      fontWeight: "600",
                      cursor: "pointer",
                      background: daysFilter === p.id ? "#ffffff" : "transparent",
                      color: daysFilter === p.id ? "#1e293b" : "#64748b",
                      boxShadow: daysFilter === p.id ? "0 1px 3px rgba(0,0,0,0.08)" : "none"
                    }}
                  >
                    {p.label}
                  </button>
                ))}
              </div>

              {daysFilter === "custom" && (
                <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    style={{ fontSize: "12px", padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: "6px" }}
                  />
                  <span style={{ fontSize: "12px", color: "#64748b" }}>to</span>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    style={{ fontSize: "12px", padding: "4px 8px", border: "1px solid #cbd5e1", borderRadius: "6px" }}
                  />
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={loadOverviewData}
                    style={{ fontSize: "12px", padding: "4px 10px" }}
                  >
                    Apply
                  </button>
                </div>
              )}

              {/* Class Filter */}
              {classrooms.length > 0 && (
                <select
                  value={selectedClassroomId}
                  onChange={(e) => setSelectedClassroomId(e.target.value)}
                  style={{
                    fontSize: "13px",
                    padding: "6px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    background: "#ffffff",
                    color: "#1e293b"
                  }}
                >
                  <option value="">All Classes</option>
                  {classrooms.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Right: Search Input & Batch Action */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  fontSize: "13px",
                  padding: "6px 12px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  width: "180px"
                }}
              />
              <button
                type="button"
                className="secondary-button"
                onClick={loadOverviewData}
                title="Refresh"
                style={{ padding: "6px 10px", fontSize: "13px" }}
              >
                🔄
              </button>
            </div>
          </div>

          {/* Current Period Label & Multi-Select Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ fontSize: "13px", color: "#475569", fontWeight: "600" }}>
              Showing performance for: <span style={{ color: "#1e40af", fontWeight: "700" }}>{periodLabel}</span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", cursor: "pointer", color: "#334155" }}>
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleToggleSelectAll}
                  style={{ width: "16px", height: "16px", cursor: "pointer" }}
                />
                Select All ({filteredStudents.length})
              </label>

              {selectedUids.length > 0 && (
                <button
                  type="button"
                  className="primary-button"
                  onClick={() => openDispatchModalFor(students.filter((s) => selectedUids.includes(s.studentUid)))}
                  style={{ fontSize: "13px", padding: "7px 16px", background: "#4f46e5" }}
                >
                  ✉️ Prepare Feedback for Selected ({selectedUids.length})
                </button>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div style={{ padding: "14px", background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", borderRadius: "8px", marginBottom: "16px" }}>
              ⚠️ {error}
            </div>
          )}

          {/* Loading Skeleton / Empty State */}
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#64748b" }}>
              <div style={{ fontSize: "28px", marginBottom: "10px" }}>⏳</div>
              <div>Aggregating student coursework and curriculum frame practice...</div>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div style={{ textAlign: "center", padding: "50px 20px", background: "#ffffff", border: "1px dashed #cbd5e1", borderRadius: "12px", color: "#64748b" }}>
              <div style={{ fontSize: "32px", marginBottom: "8px" }}>👥</div>
              <h3 style={{ margin: "0 0 6px 0", color: "#1e293b" }}>No Students Found</h3>
              <p style={{ margin: 0, fontSize: "14px" }}>
                No active students matched the selected filters. Invite students or assign coursework to begin tracking.
              </p>
            </div>
          ) : (
            /* Student Cards List */
            <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              {filteredStudents.map((s) => {
                const isSelected = selectedUids.includes(s.studentUid);
                const hasParentEmail = Boolean(s.parentEmail && s.parentEmail.includes("@"));

                return (
                  <div
                    key={s.studentUid}
                    style={{
                      background: "#ffffff",
                      border: isSelected ? "2px solid #6366f1" : "1px solid #e2e8f0",
                      borderRadius: "12px",
                      padding: "18px 20px",
                      boxShadow: isSelected ? "0 4px 12px rgba(99,102,241,0.1)" : "0 1px 3px rgba(0,0,0,0.03)",
                      transition: "all 0.15s ease"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "16px" }}>
                      
                      {/* Left: Checkbox + Student Profile & Parent Email */}
                      <div style={{ display: "flex", gap: "14px", alignItems: "flex-start" }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleSelectStudent(s.studentUid)}
                          style={{ width: "18px", height: "18px", marginTop: "3px", cursor: "pointer" }}
                        />
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                            <span style={{ fontSize: "16px", fontWeight: "700", color: "#0f172a" }}>
                              {s.name}
                            </span>
                            <span style={{ fontSize: "11px", fontWeight: "600", background: "#f1f5f9", color: "#475569", padding: "2px 8px", borderRadius: "10px" }}>
                              {s.grade}
                            </span>
                          </div>

                          {/* Parent Email Badge / Trigger */}
                          <div style={{ display: "flex", alignItems: "center", gap: "6px", marginTop: "4px" }}>
                            {hasParentEmail ? (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#1e40af",
                                  background: "#eff6ff",
                                  padding: "2px 8px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px"
                                }}
                              >
                                ✉️ Parent: {s.parentEmail}
                              </span>
                            ) : (
                              <span style={{ fontSize: "12px", color: "#64748b" }}>
                                No parent email linked (manage in <span style={{ cursor: "pointer", color: "#4f46e5", textDecoration: "underline", fontWeight: "600" }} onClick={() => navigate("/tutor/students")}>Roster</span>)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Right: Quick Action Button */}
                      <div>
                        <button
                          type="button"
                          className="secondary-button"
                          onClick={() => openDispatchModalFor([s])}
                          style={{ fontSize: "13px", padding: "6px 14px", display: "flex", alignItems: "center", gap: "6px" }}
                        >
                          ✉️ Prepare Feedback
                        </button>
                      </div>
                    </div>

                    {/* Middle: Coursework vs Self-Study Metrics */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: "12px",
                        marginTop: "16px",
                        padding: "12px 14px",
                        background: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #f1f5f9"
                      }}
                    >
                      {/* Coursework Block */}
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                          Coursework & Tests
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", marginTop: "3px" }}>
                          {s.courseworkStats.submittedCount} / {s.courseworkStats.totalAssigned} Submitted
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          {s.courseworkStats.testAvg !== null && (
                            <span style={{ color: s.courseworkStats.testAvg >= 75 ? "#16a34a" : s.courseworkStats.testAvg >= 50 ? "#d97706" : "#dc2626", fontWeight: "700" }}>
                              Test Avg: {s.courseworkStats.testAvg}% &nbsp;•&nbsp;
                            </span>
                          )}
                          {s.courseworkStats.homeworkAvg !== null ? `HW Avg: ${s.courseworkStats.homeworkAvg}%` : "No graded tests"}
                        </div>
                      </div>

                      {/* Curriculum Frames (Self Study) Block */}
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                          Self-Study Curriculum
                        </div>
                        <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e293b", marginTop: "3px" }}>
                          {s.curriculumPractice.framesAttemptedCount} Frames Attempted
                        </div>
                        <div style={{ fontSize: "12px", color: "#64748b", marginTop: "2px" }}>
                          {s.curriculumPractice.framesMasteredCount > 0 && (
                            <span style={{ color: "#16a34a", fontWeight: "600" }}>
                              {s.curriculumPractice.framesMasteredCount} Mastered &nbsp;•&nbsp;
                            </span>
                          )}
                          {s.curriculumPractice.chapters?.length ? s.curriculumPractice.chapters.slice(0, 2).join(", ") : "No active chapter practice"}
                        </div>
                      </div>

                      {/* Strengths & Focus Points */}
                      <div>
                        <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", letterSpacing: "0.5px" }}>
                          Top Strengths & Focus
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginTop: "4px" }}>
                          {(s.strengths || []).slice(0, 2).map((st, i) => (
                            <span key={i} style={{ background: "#dcfce7", color: "#15803d", fontSize: "11px", fontWeight: "600", padding: "1px 6px", borderRadius: "4px" }}>
                              ✓ {st}
                            </span>
                          ))}
                          {(s.focusAreas || []).slice(0, 1).map((fa, i) => (
                            <span key={i} style={{ background: "#fef3c7", color: "#b45309", fontSize: "11px", fontWeight: "600", padding: "1px 6px", borderRadius: "4px" }}>
                              ! {fa}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Sticky Bottom Batch Bar */}
          {selectedUids.length > 0 && (
            <div
              style={{
                position: "fixed",
                bottom: "24px",
                left: "50%",
                transform: "translateX(-50%)",
                background: "#0f172a",
                color: "#ffffff",
                padding: "12px 24px",
                borderRadius: "30px",
                boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)",
                display: "flex",
                alignItems: "center",
                gap: "18px",
                zIndex: 900
              }}
            >
              <span style={{ fontSize: "14px", fontWeight: "600" }}>
                Selected: <strong>{selectedUids.length}</strong> of {filteredStudents.length} students
              </span>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setSelectedUids([])}
                style={{ fontSize: "12px", padding: "5px 12px", background: "#334155", color: "#ffffff", border: "none" }}
              >
                Clear
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={() => openDispatchModalFor(students.filter((s) => selectedUids.includes(s.studentUid)))}
                style={{ fontSize: "13px", padding: "8px 18px", background: "#4f46e5" }}
              >
                ✉️ Review & Dispatch to Selected ({selectedUids.length})
              </button>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* TAB 2: DISPATCHED FEEDBACK HISTORY (HIERARCHICAL DISPATCH BATCHES)      */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === "history" && (
        <div>
          {/* History Search & Controls Bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <input
              type="text"
              placeholder="Search by student, email, or period..."
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              style={{ fontSize: "13px", padding: "8px 14px", borderRadius: "8px", border: "1px solid #cbd5e1", width: "280px" }}
            />
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              {filteredBatches.length > 0 && (
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => handleToggleAllBatches(expandedBatchIds.size !== filteredBatches.length)}
                  style={{ fontSize: "13px", padding: "6px 14px" }}
                >
                  {expandedBatchIds.size === filteredBatches.length ? "Collapse All ▲" : "Expand All ▼"}
                </button>
              )}
              <button
                type="button"
                className="secondary-button"
                onClick={loadHistoryData}
                style={{ fontSize: "13px", padding: "6px 14px" }}
              >
                🔄 Refresh History
              </button>
            </div>
          </div>

          {historyLoading ? (
            <div style={{ textAlign: "center", padding: "50px 20px", color: "#64748b" }}>
              ⏳ Loading dispatched reports history...
            </div>
          ) : historyBatches.length === 0 ? (
            <div style={{ textAlign: "center", padding: "60px 20px", background: "#ffffff", border: "1px dashed #cbd5e1", borderRadius: "12px", color: "#64748b" }}>
              <div style={{ fontSize: "36px", marginBottom: "8px" }}>📭</div>
              <h3 style={{ margin: "0 0 6px 0", color: "#1e293b" }}>No Dispatched Reports Yet</h3>
              <p style={{ margin: 0, fontSize: "14px" }}>
                Switch to the "Student Performance & Dispatch" tab to select students and send their first parent update.
              </p>
            </div>
          ) : filteredBatches.length === 0 ? (
            <div style={{ textAlign: "center", padding: "40px 20px", background: "#ffffff", border: "1px dashed #cbd5e1", borderRadius: "12px", color: "#64748b" }}>
              <div style={{ fontSize: "28px", marginBottom: "6px" }}>🔍</div>
              <h4 style={{ margin: "0 0 6px 0", color: "#1e293b" }}>No feedback dispatches match "{historySearch}"</h4>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setHistorySearch("")}
                style={{ fontSize: "12px", padding: "4px 10px", marginTop: "8px" }}
              >
                Clear Search
              </button>
            </div>
          ) : (
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.03)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" }}>
                <thead>
                  <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0", color: "#475569", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px" }}>
                    <th style={{ width: "44px", padding: "12px 14px", textAlign: "center" }}></th>
                    <th style={{ padding: "12px 16px" }}>Dispatch Date & Time</th>
                    <th style={{ padding: "12px 16px" }}>Recipients / Audience</th>
                    <th style={{ padding: "12px 16px" }}>Period Covered</th>
                    <th style={{ padding: "12px 16px" }}>Delivery & Read Receipts</th>
                    <th style={{ padding: "12px 16px", textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBatches.map((batch) => {
                    const isExpanded = expandedBatchIds.has(batch.batchId);
                    const sentDate = new Date(batch.sentAt);
                    const sentDateStr = sentDate.toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit"
                    });

                    return (
                      <React.Fragment key={batch.batchId}>
                        {/* ── PARENT ROW: DISPATCH EVENT ── */}
                        <tr
                          onClick={() => toggleBatchExpand(batch.batchId)}
                          style={{
                            borderBottom: isExpanded ? "1px solid #e2e8f0" : "1px solid #f1f5f9",
                            background: isExpanded ? "#f8fafc" : "#ffffff",
                            cursor: "pointer",
                            transition: "background 0.15s ease"
                          }}
                        >
                          {/* Toggle Expand Arrow */}
                          <td style={{ padding: "14px 12px 14px 16px", textAlign: "center" }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleBatchExpand(batch.batchId);
                              }}
                              aria-label={isExpanded ? "Collapse dispatch batch" : "Expand dispatch batch"}
                              style={{
                                background: isExpanded ? "#e0e7ff" : "#f1f5f9",
                                color: isExpanded ? "#4338ca" : "#64748b",
                                border: "none",
                                borderRadius: "6px",
                                width: "26px",
                                height: "26px",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                cursor: "pointer",
                                fontSize: "11px",
                                transition: "all 0.15s ease"
                              }}
                            >
                              {isExpanded ? "▼" : "▶"}
                            </button>
                          </td>

                          {/* Date Sent */}
                          <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                            <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "13px" }}>
                              {sentDateStr}
                            </div>
                            <div style={{ fontSize: "11px", color: "#94a3b8", marginTop: "2px" }}>
                              Batch #{String(batch.batchId).slice(-6)}
                            </div>
                          </td>

                          {/* Recipients / Audience */}
                          <td style={{ padding: "14px 16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                              <span
                                style={{
                                  background: "#eef2ff",
                                  color: "#4338ca",
                                  padding: "3px 8px",
                                  borderRadius: "12px",
                                  fontSize: "11px",
                                  fontWeight: "700"
                                }}
                              >
                                👥 {batch.studentCount} {batch.studentCount === 1 ? "Student" : "Students"}
                              </span>
                              <span style={{ fontWeight: "600", color: "#1e293b", fontSize: "13px" }}>
                                {batch.studentNames?.join(", ") || "Students"}
                              </span>
                            </div>
                            {batch.classroomName && (
                              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "3px" }}>
                                Class: {batch.classroomName}
                              </div>
                            )}
                          </td>

                          {/* Period */}
                          <td style={{ padding: "14px 16px", color: "#475569", whiteSpace: "nowrap" }}>
                            {batch.periodLabel}
                          </td>

                          {/* Delivery & Read Receipts */}
                          <td style={{ padding: "14px 16px", whiteSpace: "nowrap" }}>
                            {batch.failedCount > 0 ? (
                              <span style={{ background: "#fee2e2", color: "#991b1b", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                ⚠️ {batch.failedCount} Failed
                              </span>
                            ) : batch.readCount === batch.studentCount && batch.studentCount > 0 ? (
                              <span style={{ background: "#dcfce7", color: "#15803d", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                🟢 All Read ({batch.readCount}/{batch.studentCount})
                              </span>
                            ) : batch.readCount > 0 ? (
                              <span style={{ background: "#dcfce7", color: "#15803d", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                🟢 {batch.readCount} of {batch.studentCount} Read
                              </span>
                            ) : batch.deliveredCount > 0 ? (
                              <span style={{ background: "#e0f2fe", color: "#0369a1", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                🔵 {batch.deliveredCount} Sent / Delivered
                              </span>
                            ) : (
                              <span style={{ background: "#f1f5f9", color: "#64748b", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                ⚪ Sent
                              </span>
                            )}
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "14px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              className="secondary-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleBatchExpand(batch.batchId);
                              }}
                              style={{
                                fontSize: "12px",
                                padding: "4px 10px",
                                background: isExpanded ? "#e2e8f0" : "#ffffff",
                                fontWeight: "600"
                              }}
                            >
                              {isExpanded ? "Collapse ▲" : `Unfold (${batch.studentCount}) ▼`}
                            </button>
                          </td>
                        </tr>

                        {/* ── UNFOLDED CHILDREN ROWS: INDIVIDUAL STUDENTS & PARENTS ── */}
                        {isExpanded && (
                          <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                            <td colSpan={6} style={{ padding: "4px 16px 18px 44px" }}>
                              <div
                                style={{
                                  background: "#ffffff",
                                  border: "1px solid #e2e8f0",
                                  borderLeft: "4px solid #4f46e5",
                                  borderRadius: "10px",
                                  overflow: "hidden",
                                  boxShadow: "0 1px 3px rgba(0,0,0,0.04)"
                                }}
                              >
                                {/* Batch Header Strip */}
                                {(batch.overallSummary || batch.tutorPersonalNote) && (
                                  <div
                                    style={{
                                      padding: "10px 16px",
                                      background: "#faf5ff",
                                      borderBottom: "1px solid #f1f5f9",
                                      fontSize: "12px",
                                      color: "#475569",
                                      lineHeight: "1.5"
                                    }}
                                  >
                                    {batch.overallSummary && (
                                      <div>
                                        <strong style={{ color: "#6b21a8" }}>Executive Summary:</strong> {batch.overallSummary}
                                      </div>
                                    )}
                                    {batch.tutorPersonalNote && (
                                      <div style={{ marginTop: "4px" }}>
                                        <strong style={{ color: "#4338ca" }}>Tutor Note:</strong> {batch.tutorPersonalNote}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Children Sub-Table */}
                                <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "13px" }}>
                                  <thead>
                                    <tr
                                      style={{
                                        background: "#f1f5f9",
                                        borderBottom: "1px solid #e2e8f0",
                                        color: "#475569",
                                        fontSize: "11px",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px"
                                      }}
                                    >
                                      <th style={{ padding: "10px 16px" }}>Student</th>
                                      <th style={{ padding: "10px 16px" }}>Parent Contact</th>
                                      <th style={{ padding: "10px 16px" }}>Class / Batch</th>
                                      <th style={{ padding: "10px 16px" }}>Read Status</th>
                                      <th style={{ padding: "10px 16px", textAlign: "right" }}>Snapshot Action</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {batch.children.map((childReport) => {
                                      const isChildRead = childReport.readStatus === "read";
                                      const childReadDateStr = childReport.readAt
                                        ? new Date(childReport.readAt).toLocaleDateString("en-US", {
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit"
                                          })
                                        : null;

                                      return (
                                        <tr
                                          key={childReport._id}
                                          style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            background: "#ffffff"
                                          }}
                                        >
                                          {/* Student */}
                                          <td style={{ padding: "12px 16px", fontWeight: "700", color: "#0f172a" }}>
                                            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                              <span
                                                style={{
                                                  width: "24px",
                                                  height: "24px",
                                                  borderRadius: "50%",
                                                  background: "#e0e7ff",
                                                  color: "#4338ca",
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  justifyContent: "center",
                                                  fontSize: "11px",
                                                  fontWeight: "800"
                                                }}
                                              >
                                                {(childReport.studentName || "S")[0].toUpperCase()}
                                              </span>
                                              <span>{childReport.studentName || "Student"}</span>
                                            </div>
                                          </td>

                                          {/* Parent Contact */}
                                          <td style={{ padding: "12px 16px", color: "#334155" }}>
                                            <div>{childReport.parentEmail}</div>
                                            {childReport.parentName && (
                                              <div style={{ fontSize: "11px", color: "#64748b" }}>
                                                Parent: {childReport.parentName}
                                              </div>
                                            )}
                                          </td>

                                          {/* Class / Batch */}
                                          <td style={{ padding: "12px 16px", color: "#475569" }}>
                                            {childReport.classroomName || "—"}
                                          </td>

                                          {/* Read Status */}
                                          <td style={{ padding: "12px 16px" }}>
                                            {isChildRead ? (
                                              <span
                                                style={{
                                                  display: "inline-flex",
                                                  alignItems: "center",
                                                  gap: "4px",
                                                  background: "#dcfce7",
                                                  color: "#15803d",
                                                  padding: "3px 8px",
                                                  borderRadius: "12px",
                                                  fontSize: "11px",
                                                  fontWeight: "700"
                                                }}
                                                title={`Opened at: ${childReadDateStr}`}
                                              >
                                                🟢 Read {childReport.openCount > 1 ? `(${childReport.openCount}x)` : ""}
                                                {childReadDateStr && (
                                                  <span style={{ fontSize: "10px", color: "#166534", fontWeight: "normal" }}>
                                                    • {childReadDateStr}
                                                  </span>
                                                )}
                                              </span>
                                            ) : childReport.deliveryStatus === "delivered" ? (
                                              <span style={{ background: "#e0f2fe", color: "#0369a1", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                                🔵 Delivered (Unread)
                                              </span>
                                            ) : childReport.deliveryStatus === "failed" ? (
                                              <span style={{ background: "#fee2e2", color: "#991b1b", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                                🔴 Failed
                                              </span>
                                            ) : (
                                              <span style={{ background: "#f1f5f9", color: "#64748b", padding: "3px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: "700" }}>
                                                ⚪ Sent
                                              </span>
                                            )}
                                          </td>

                                          {/* Actions */}
                                          <td style={{ padding: "12px 16px", textAlign: "right" }}>
                                            <button
                                              type="button"
                                              className="secondary-button"
                                              onClick={() => setSelectedSnapshot(childReport)}
                                              style={{
                                                fontSize: "12px",
                                                padding: "4px 10px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "5px"
                                              }}
                                            >
                                              👁️ View Report
                                            </button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* MODAL: DISPATCH COMPOSER                                               */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {showDispatchModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "680px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "26px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)"
            }}
          >
            {/* Modal Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "14px", marginBottom: "18px" }}>
              <div>
                <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "800", color: "#0f172a" }}>
                  ✉️ Review & Send Parent Progress Report
                </h2>
                <div style={{ fontSize: "13px", color: "#64748b", marginTop: "3px" }}>
                  Period: <strong>{periodLabel}</strong> &nbsp;•&nbsp; Recipients: <strong>{dispatchTargets.length} Student(s)</strong>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDispatchModal(false)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}
              >
                ✕
              </button>
            </div>

            {/* Recipient Validation Warning if missing email */}
            {dispatchTargets.some((t) => !t.parentEmail || !t.parentEmail.includes("@")) && (
              <div style={{ padding: "12px", background: "#fef3c7", border: "1px solid #fcd34d", color: "#92400e", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" }}>
                ⚠️ One or more selected students do not have a parent email linked. Reports will only be delivered to valid parent email addresses.
              </div>
            )}

            {/* Recipient Pills */}
            <div style={{ marginBottom: "16px" }}>
              <div style={{ fontSize: "12px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", marginBottom: "6px" }}>
                Target Recipients
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {dispatchTargets.map((t) => (
                  <span
                    key={t.studentUid}
                    style={{
                      background: t.parentEmail ? "#f0fdf4" : "#fef2f2",
                      border: t.parentEmail ? "1px solid #bbf7d0" : "1px solid #fecaca",
                      color: t.parentEmail ? "#166534" : "#991b1b",
                      padding: "4px 10px",
                      borderRadius: "14px",
                      fontSize: "12px",
                      fontWeight: "600"
                    }}
                  >
                    {t.name} ({t.parentEmail || "No Email"})
                  </span>
                ))}
              </div>
            </div>

            {/* AI Summary / Executive Overview */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#1e293b", marginBottom: "6px" }}>
                Executive Overview (AI Drafted, Editable):
              </label>
              <textarea
                rows={3}
                value={sharedSummary}
                onChange={(e) => setSharedSummary(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", lineHeight: "1.5" }}
              />
            </div>

            {/* Teacher's Guidance / Personal Remarks */}
            <div style={{ marginBottom: "18px" }}>
              <label style={{ display: "block", fontSize: "13px", fontWeight: "700", color: "#1e293b", marginBottom: "4px" }}>
                👨‍🏫 Teacher's Guidance & Personal Note to Parents:
              </label>
              <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "6px" }}>
                This note will be prominently highlighted in the email sent to the parents.
              </div>
              <textarea
                rows={4}
                placeholder="e.g. Arun has shown solid improvement in factorization methods this week. Please encourage him to review signs carefully before submitting answers."
                value={sharedTutorNote}
                onChange={(e) => setSharedTutorNote(e.target.value)}
                style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "13px", lineHeight: "1.5" }}
              />
            </div>

            {/* Preview of Coursework & Curriculum included */}
            {dispatchTargets.length === 1 && (
              <div style={{ background: "#f8fafc", padding: "14px", borderRadius: "8px", border: "1px solid #e2e8f0", marginBottom: "18px" }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", marginBottom: "8px" }}>
                  📋 Content Breakdown Preview for {dispatchTargets[0].name}
                </div>
                <div style={{ fontSize: "13px", color: "#1e293b" }}>
                  <strong>Coursework:</strong> {dispatchTargets[0].courseworkStats?.submittedCount || 0} submitted, {dispatchTargets[0].courseworkStats?.notAttemptedCount || 0} unattempted.
                  <br />
                  <strong>Curriculum Frames:</strong> {dispatchTargets[0].curriculumPractice?.framesAttemptedCount || 0} questions practiced.
                </div>
              </div>
            )}

            {/* Dispatch Result Summary (if finished) */}
            {dispatchResult && (
              <div style={{ padding: "12px", background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", borderRadius: "8px", marginBottom: "16px", fontSize: "13px" }}>
                ✓ Dispatched {dispatchResult.dispatchedCount} report(s) successfully!
                {dispatchResult.failedCount > 0 && ` (${dispatchResult.failedCount} skipped due to missing email)`}
              </div>
            )}

            {/* Modal Actions */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button
                type="button"
                className="secondary-button"
                onClick={() => setShowDispatchModal(false)}
                disabled={dispatching}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleDispatchReports}
                disabled={dispatching || !dispatchTargets.some((t) => t.parentEmail)}
                style={{ background: "#4f46e5", display: "flex", alignItems: "center", gap: "6px" }}
              >
                {dispatching ? "Sending via Resend..." : `🚀 Dispatch via Resend (${dispatchTargets.filter((t) => t.parentEmail).length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════ */}
      {/* MODAL: VIEW DISPATCHED SNAPSHOT                                        */}
      {/* ══════════════════════════════════════════════════════════════════════ */}
      {selectedSnapshot && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            padding: "20px"
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "620px",
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              padding: "24px",
              boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.2)"
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "12px", marginBottom: "16px" }}>
              <div>
                <h3 style={{ margin: 0, color: "#0f172a" }}>Report Snapshot: {selectedSnapshot.studentName}</h3>
                <div style={{ fontSize: "12px", color: "#64748b" }}>
                  To: {selectedSnapshot.parentEmail} &nbsp;•&nbsp; Period: {selectedSnapshot.periodLabel}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedSnapshot(null)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#94a3b8" }}
              >
                ✕
              </button>
            </div>

            {/* Read Receipt Banner */}
            <div
              style={{
                padding: "10px 14px",
                background: selectedSnapshot.readStatus === "read" ? "#dcfce7" : "#f1f5f9",
                borderRadius: "8px",
                marginBottom: "16px",
                fontSize: "13px",
                color: selectedSnapshot.readStatus === "read" ? "#15803d" : "#475569",
                fontWeight: "600"
              }}
            >
              {selectedSnapshot.readStatus === "read"
                ? `🟢 Opened by Parent on ${new Date(selectedSnapshot.readAt).toLocaleString()} (${selectedSnapshot.openCount} views)`
                : "⚪ Delivered (Not yet opened by parent)"}
            </div>

            {/* Executive Summary */}
            {selectedSnapshot.overallSummary && (
              <div style={{ marginBottom: "14px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b" }}>
                  Executive Summary
                </div>
                <div style={{ fontSize: "13px", color: "#334155", marginTop: "4px", background: "#f8fafc", padding: "10px", borderRadius: "6px" }}>
                  {selectedSnapshot.overallSummary}
                </div>
              </div>
            )}

            {/* Teacher's Note */}
            {selectedSnapshot.tutorPersonalNote && (
              <div style={{ marginBottom: "16px", padding: "12px", background: "#eef2ff", borderLeft: "4px solid #4f46e5", borderRadius: "4px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#4338ca", marginBottom: "4px" }}>
                  Teacher's Guidance
                </div>
                <div style={{ fontSize: "13px", color: "#1e1b4b", whiteSpace: "pre-wrap" }}>
                  {selectedSnapshot.tutorPersonalNote}
                </div>
              </div>
            )}

            {/* Assignment Scores Table */}
            {selectedSnapshot.assignmentScores && selectedSnapshot.assignmentScores.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", color: "#64748b", marginBottom: "6px" }}>
                  Coursework & Tests Delivered in Report
                </div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                  <tbody>
                    {selectedSnapshot.assignmentScores.map((a, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                        <td style={{ padding: "8px 4px", fontWeight: "600" }}>{a.title} ({a.assignmentType})</td>
                        <td style={{ padding: "8px 4px", textAlign: "right" }}>
                          {a.status === "not_attempted" ? (
                            <span style={{ color: "#ef4444" }}>Not Attempted</span>
                          ) : (
                            <span>{a.totalScore}/{a.maxScore} ({a.percentage}%)</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Curriculum Stats */}
            {selectedSnapshot.curriculumPractice && (
              <div style={{ padding: "10px", background: "#f0fdf4", borderRadius: "6px", fontSize: "12px", color: "#166534" }}>
                <strong>Self-Study:</strong> {selectedSnapshot.curriculumPractice.framesAttemptedCount || 0} questions across {(selectedSnapshot.curriculumPractice.chapters || []).join(", ") || "Chapters"}
              </div>
            )}

            <div style={{ textAlign: "right", marginTop: "18px" }}>
              <button
                type="button"
                className="primary-button"
                onClick={() => setSelectedSnapshot(null)}
              >
                Close Snapshot
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

